import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Save, Sync, Refresh } from '@mui/icons-material';
import { toast } from 'react-toastify';
import BillingNav from './BillingNav';
import {
  BillingPage,
  BillingPanel,
  BillingTableHead,
  StatusChip,
  btnGhost,
  btnPrimary,
  billingSx,
  tones,
} from './billingUi';
import {
  createPricingPlan,
  fetchPricingPlans,
  fetchProductMaster,
  formatMoney,
  syncPricingPlanFromProductMaster,
  updatePricingPlan,
  upsertPricingItem,
} from '../../services/billingApi';
import { fetchLookups } from '../../services/licensingApi';

const emptyPlan = {
  planCode: '',
  planName: '',
  billingFrequency: 'MONTHLY',
  currency: 'USD',
  effectiveFrom: '',
  effectiveTo: '',
  customPeriodDays: '',
  active: true,
};

const emptyItem = {
  pricingItemId: null,
  productCode: 'HUB',
  chargeCode: '',
  chargeName: '',
  productPricingType: 'SUBSCRIPTION',
  chargeType: 'FIXED',
  includedQuantity: '',
  unitPrice: '0',
  monthlyUnitPrice: '0',
  yearlyUnitPrice: '',
  overagePrice: '',
  unit: '',
  active: true,
};

const PRODUCT_PRICING_TYPES = [
  { code: 'SUBSCRIPTION', label: 'Subscription (Monthly / Yearly)' },
  { code: 'USAGE_BASED', label: 'Per-unit / usage' },
  { code: 'FIXED_AMOUNT', label: 'Fixed amount' },
];

const pricingTypeLabel = (code) =>
  PRODUCT_PRICING_TYPES.find((t) => t.code === code)?.label || code || '—';

/** Normalize lookup rows so Select value/label are always strings. */
function lookupOptions(rows, fallback) {
  const list = Array.isArray(rows) && rows.length ? rows : fallback;
  return (list || [])
    .map((c) => {
      if (c == null) return null;
      if (typeof c === 'string' || typeof c === 'number') {
        const code = String(c);
        return { code, label: code };
      }
      const code = String(c.code ?? c.lookupCode ?? c.value ?? '').trim();
      if (!code) return null;
      return { code, label: String(c.label ?? c.lookupLabel ?? c.name ?? code) };
    })
    .filter(Boolean);
}

const PricingPlans = () => {
  const [plans, setPlans] = useState([]);
  const [lookups, setLookups] = useState({});
  const [productMaster, setProductMaster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [planDialog, setPlanDialog] = useState(false);
  const [itemDialog, setItemDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState(emptyPlan);
  const [itemForm, setItemForm] = useState(emptyItem);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncingId, setSyncingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [p, luk, pm] = await Promise.all([
        fetchPricingPlans(true),
        fetchLookups({ grouped: true }),
        fetchProductMaster().catch(() => []),
      ]);
      setPlans(p);
      setLookups(luk || {});
      setProductMaster(Array.isArray(pm) ? pm : []);
    } catch (e) {
      setError(e.message || 'Failed to load pricing plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const products = useMemo(() => {
    if (productMaster.length) {
      return productMaster.map((p) => ({
        code: String(p.productCode || p.product_code),
        label: String(p.productName || p.product_name || p.productCode),
      }));
    }
    return lookupOptions(lookups.PRODUCT, [
      { code: 'HUB', label: 'Hub' },
      { code: 'SYNC_FILE', label: 'Sync File' },
      { code: 'AGENTIC_AI', label: 'Agentic AI' },
      { code: 'PLATFORM', label: 'Platform' },
    ]);
  }, [productMaster, lookups.PRODUCT]);

  const frequencies = lookupOptions(lookups.BILLING_FREQUENCY, [
    { code: 'MONTHLY', label: 'Monthly' },
    { code: 'ANNUAL', label: 'Annual' },
  ]);
  const currencies = lookupOptions(lookups.CURRENCY, [
    { code: 'USD', label: 'USD' },
    { code: 'EUR', label: 'EUR' },
    { code: 'GBP', label: 'GBP' },
  ]);

  const openCreatePlan = () => {
    setEditingPlan(null);
    setPlanForm(emptyPlan);
    setPlanDialog(true);
  };

  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      planCode: plan.planCode || '',
      planName: plan.planName || '',
      billingFrequency: String(plan.billingFrequency || 'MONTHLY'),
      currency: String(plan.currency || 'USD'),
      effectiveFrom: plan.effectiveFrom ? String(plan.effectiveFrom).slice(0, 10) : '',
      effectiveTo: plan.effectiveTo ? String(plan.effectiveTo).slice(0, 10) : '',
      customPeriodDays: plan.customPeriodDays != null ? String(plan.customPeriodDays) : '',
      active: plan.active !== false,
    });
    setPlanDialog(true);
  };

  const savePlan = async () => {
    setSaving(true);
    try {
      if (editingPlan?.pricingPlanId) {
        await updatePricingPlan(editingPlan.pricingPlanId, planForm);
        toast.success('Pricing plan updated');
      } else {
        await createPricingPlan(planForm);
        toast.success('Pricing plan created');
      }
      setPlanDialog(false);
      await load();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const syncFromProductMaster = async (plan) => {
    if (!plan?.pricingPlanId) return;
    setSyncingId(plan.pricingPlanId);
    try {
      const r = await syncPricingPlanFromProductMaster(plan.pricingPlanId);
      toast.success(r.message || `Synced ${r.syncedFromProductMaster} lines`);
      await load();
    } catch (e) {
      toast.error(e.message || 'Sync failed');
    } finally {
      setSyncingId(null);
    }
  };

  const openItem = (plan, item = null) => {
    setSelectedPlanId(plan.pricingPlanId);
    if (item) {
      const pt =
        item.productPricingType ||
        (item.chargeType === 'USAGE' || item.chargeType === 'PER_UNIT'
          ? 'USAGE_BASED'
          : item.chargeType === 'ONE_TIME'
            ? 'FIXED_AMOUNT'
            : 'SUBSCRIPTION');
      const monthly =
        item.monthlyUnitPrice != null
          ? String(item.monthlyUnitPrice)
          : item.unitPrice != null
            ? String(item.unitPrice)
            : '0';
      setItemForm({
        pricingItemId: item.pricingItemId,
        productCode: String(item.productCode || 'HUB'),
        chargeCode: item.chargeCode || '',
        chargeName: item.chargeName || '',
        productPricingType: pt,
        chargeType: String(item.chargeType || 'FIXED'),
        includedQuantity: item.includedQuantity != null ? String(item.includedQuantity) : '',
        unitPrice: item.unitPrice != null ? String(item.unitPrice) : monthly,
        monthlyUnitPrice: monthly,
        yearlyUnitPrice: item.yearlyUnitPrice != null ? String(item.yearlyUnitPrice) : '',
        overagePrice: item.overagePrice != null ? String(item.overagePrice) : '',
        unit: item.unit || '',
        active: item.active !== false,
      });
    } else {
      setItemForm({
        ...emptyItem,
        productCode: products[0]?.code || 'HUB',
      });
    }
    setItemDialog(true);
  };

  const saveItem = async () => {
    if (!selectedPlanId) return;
    if (!itemForm.chargeCode?.trim()) {
      toast.error('Charge code is required');
      return;
    }
    if (itemForm.productPricingType === 'USAGE_BASED' && !String(itemForm.unit || '').trim()) {
      toast.error('Per-unit / usage requires a Unit (GB, MB, Hours…)');
      return;
    }
    setSaving(true);
    try {
      const monthly =
        itemForm.productPricingType === 'SUBSCRIPTION'
          ? itemForm.monthlyUnitPrice
          : itemForm.unitPrice;
      await upsertPricingItem(selectedPlanId, {
        ...itemForm,
        unitPrice: monthly,
        monthlyUnitPrice: itemForm.productPricingType === 'SUBSCRIPTION' ? itemForm.monthlyUnitPrice : monthly,
        yearlyUnitPrice:
          itemForm.productPricingType === 'SUBSCRIPTION' && itemForm.yearlyUnitPrice !== ''
            ? itemForm.yearlyUnitPrice
            : null,
        includedQuantity: itemForm.includedQuantity === '' ? null : itemForm.includedQuantity,
        overagePrice: itemForm.overagePrice === '' ? null : itemForm.overagePrice,
      });
      toast.success('Pricing item saved');
      setItemDialog(false);
      await load();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const productCodes = new Set(products.map((p) => p.code));

  return (
    <BillingPage>
      <BillingNav
        title="Pricing & Plans"
        subtitle="Sync charge lines from Product Master, then set prices by Product type: Subscription (monthly/yearly), Per-unit (GB/MB/Hours), or Fixed amount."
        actions={
          <>
            <Button startIcon={<Refresh />} variant="outlined" sx={btnGhost} onClick={load}>
              Refresh
            </Button>
            <Button variant="contained" startIcon={<Add />} sx={btnPrimary} onClick={openCreatePlan}>
              New plan
            </Button>
          </>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : plans.length === 0 ? (
        <BillingPanel padded>
          <Typography color="text.secondary">No pricing plans. Create one to begin.</Typography>
        </BillingPanel>
      ) : (
        plans.map((plan, idx) => {
          const planTone = ['maroon', 'teal', 'blue', 'amber'][idx % 4];
          return (
          <BillingPanel key={plan.pricingPlanId} sx={{ mb: 2.5 }} tone={planTone}>
            <Box
              sx={{
                ...billingSx.panelPad,
                pb: 1.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: tones[planTone]?.soft || tones.maroon.soft,
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="h6" sx={{ fontWeight: 650, letterSpacing: '-0.01em' }}>
                    {plan.planName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {plan.planCode}
                  </Typography>
                  {plan.active === false ? (
                    <StatusChip status="EXPIRED" label="Inactive" />
                  ) : (
                    <StatusChip status="ACTIVE" label="Active" />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {plan.billingFrequency}
                  {String(plan.billingFrequency || '').toUpperCase() === 'CUSTOM' && plan.customPeriodDays
                    ? ` (${plan.customPeriodDays} days)`
                    : ''}
                  {' · '}
                  {plan.currency}
                  {plan.effectiveFrom
                    ? ` · from ${String(plan.effectiveFrom).slice(0, 10)}`
                    : ''}
                </Typography>
                {(() => {
                  const items = (plan.items || []).filter((it) => it.active !== false);
                  const priced = items.filter((it) => {
                    const rate = Number(it.monthlyUnitPrice != null ? it.monthlyUnitPrice : it.unitPrice);
                    return Number.isFinite(rate) && rate > 0;
                  });
                  const indicative = priced.reduce((sum, it) => {
                    const rate = Number(it.monthlyUnitPrice != null ? it.monthlyUnitPrice : it.unitPrice) || 0;
                    const incl = Number(it.includedQuantity);
                    const qty = Number.isFinite(incl) && incl > 0 ? incl : 1;
                    return sum + rate * qty;
                  }, 0);
                  if (priced.length === 0) return null;
                  return (
                    <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
                      Indicative monthly: {formatMoney(indicative, plan.currency)}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                        (priced lines × included qty, or 1 if blank — tenant total uses their contracted qty)
                      </Typography>
                    </Typography>
                  );
                })()}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="outlined"
                  sx={btnGhost}
                  startIcon={
                    syncingId === plan.pricingPlanId ? <CircularProgress size={14} /> : <Sync />
                  }
                  disabled={syncingId === plan.pricingPlanId}
                  onClick={() => syncFromProductMaster(plan)}
                >
                  Sync Product Master
                </Button>
                <Button size="small" sx={btnGhost} onClick={() => openEditPlan(plan)}>
                  Edit plan
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  sx={btnPrimary}
                  onClick={() => openItem(plan)}
                >
                  Add charge
                </Button>
              </Box>
            </Box>
            <TableContainer>
              <Table size="small">
                <BillingTableHead
                  columns={[
                    { label: 'Product' },
                    { label: 'Charge' },
                    { label: 'Product type' },
                    { label: 'Included', align: 'right' },
                    { label: 'Monthly / rate', align: 'right' },
                    { label: 'Yearly', align: 'right' },
                    { label: 'Unit' },
                    { label: 'Active' },
                    { label: '', align: 'right' },
                  ]}
                />
                <TableBody>
                  {(plan.items || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ py: 4, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          No charge items — Sync from Product Master, then enter prices by type.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    (plan.items || []).map((it) => {
                      const pt =
                        it.productPricingType ||
                        (it.chargeType === 'ONE_TIME'
                          ? 'FIXED_AMOUNT'
                          : it.chargeType === 'USAGE' || it.chargeType === 'PER_UNIT'
                            ? 'USAGE_BASED'
                            : 'SUBSCRIPTION');
                      const monthly =
                        it.monthlyUnitPrice != null ? it.monthlyUnitPrice : it.unitPrice;
                      return (
                      <TableRow key={it.pricingItemId} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {it.productCode}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {it.chargeName}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {it.chargeCode}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {it.productPricingTypeLabel || pricingTypeLabel(pt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {it.includedQuantity ?? '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                          {formatMoney(monthly, plan.currency)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {pt === 'SUBSCRIPTION' && it.yearlyUnitPrice != null
                            ? formatMoney(it.yearlyUnitPrice, plan.currency)
                            : '—'}
                        </TableCell>
                        <TableCell>{pt === 'USAGE_BASED' ? it.unit || '—' : '—'}</TableCell>
                        <TableCell>{it.active === false ? 'No' : 'Yes'}</TableCell>
                        <TableCell align="right">
                          <Button size="small" sx={btnGhost} onClick={() => openItem(plan, it)}>
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </BillingPanel>
          );
        })
      )}

      <Dialog open={planDialog} onClose={() => setPlanDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPlan ? 'Edit pricing plan' : 'New pricing plan'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Plan code"
                disabled={!!editingPlan}
                value={planForm.planCode}
                onChange={(e) =>
                  setPlanForm((f) => ({ ...f, planCode: e.target.value.toUpperCase() }))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Plan name"
                value={planForm.planName}
                onChange={(e) => setPlanForm((f) => ({ ...f, planName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Billing frequency</InputLabel>
                <Select
                  label="Billing frequency"
                  value={planForm.billingFrequency || ''}
                  onChange={(e) =>
                    setPlanForm((f) => ({ ...f, billingFrequency: e.target.value }))
                  }
                >
                  {frequencies.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Currency</InputLabel>
                <Select
                  label="Currency"
                  value={planForm.currency || ''}
                  onChange={(e) => setPlanForm((f) => ({ ...f, currency: e.target.value }))}
                >
                  {currencies.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.label}
                    </MenuItem>
                  ))}
                  {!currencies.some((c) => c.code === planForm.currency) && planForm.currency ? (
                    <MenuItem value={planForm.currency}>{planForm.currency}</MenuItem>
                  ) : null}
                </Select>
              </FormControl>
            </Grid>
            {String(planForm.billingFrequency || '').toUpperCase() === 'CUSTOM' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Custom period (days)"
                  value={planForm.customPeriodDays}
                  onChange={(e) => setPlanForm((f) => ({ ...f, customPeriodDays: e.target.value }))}
                  helperText="Generate Draft uses this many days, starting from the subscription start date."
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Effective from"
                InputLabelProps={{ shrink: true }}
                value={planForm.effectiveFrom}
                onChange={(e) => setPlanForm((f) => ({ ...f, effectiveFrom: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Effective to"
                InputLabelProps={{ shrink: true }}
                value={planForm.effectiveTo}
                onChange={(e) => setPlanForm((f) => ({ ...f, effectiveTo: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={planForm.active !== false}
                    onChange={(e) => setPlanForm((f) => ({ ...f, active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialog(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<Save />} disabled={saving} onClick={savePlan}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={itemDialog} onClose={() => setItemDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{itemForm.pricingItemId ? 'Edit charge' : 'Add charge'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Product</InputLabel>
                <Select
                  label="Product"
                  value={itemForm.productCode || ''}
                  onChange={(e) => setItemForm((f) => ({ ...f, productCode: e.target.value }))}
                >
                  {products.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.label}
                    </MenuItem>
                  ))}
                  {itemForm.productCode && !productCodes.has(itemForm.productCode) ? (
                    <MenuItem value={itemForm.productCode}>{itemForm.productCode}</MenuItem>
                  ) : null}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Product type</InputLabel>
                <Select
                  label="Product type"
                  value={itemForm.productPricingType || 'SUBSCRIPTION'}
                  onChange={(e) => {
                    const next = e.target.value;
                    setItemForm((f) => ({
                      ...f,
                      productPricingType: next,
                      unit: next === 'USAGE_BASED' ? f.unit || 'GB' : '',
                    }));
                  }}
                >
                  {PRODUCT_PRICING_TYPES.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Charge code"
                value={itemForm.chargeCode}
                onChange={(e) => setItemForm((f) => ({ ...f, chargeCode: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Charge name"
                value={itemForm.chargeName}
                onChange={(e) => setItemForm((f) => ({ ...f, chargeName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Included qty"
                helperText="Blank → use license entitlement on apply"
                value={itemForm.includedQuantity}
                onChange={(e) => setItemForm((f) => ({ ...f, includedQuantity: e.target.value }))}
              />
            </Grid>
            {itemForm.productPricingType === 'SUBSCRIPTION' ? (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Monthly price"
                    value={itemForm.monthlyUnitPrice}
                    onChange={(e) =>
                      setItemForm((f) => ({
                        ...f,
                        monthlyUnitPrice: e.target.value,
                        unitPrice: e.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Yearly price"
                    value={itemForm.yearlyUnitPrice}
                    onChange={(e) => setItemForm((f) => ({ ...f, yearlyUnitPrice: e.target.value }))}
                  />
                </Grid>
              </>
            ) : itemForm.productPricingType === 'USAGE_BASED' ? (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Price per unit"
                    value={itemForm.unitPrice}
                    onChange={(e) => setItemForm((f) => ({ ...f, unitPrice: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Unit (GB, MB, Hours…)"
                    required
                    value={itemForm.unit}
                    onChange={(e) => setItemForm((f) => ({ ...f, unit: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Overage price"
                    value={itemForm.overagePrice}
                    onChange={(e) => setItemForm((f) => ({ ...f, overagePrice: e.target.value }))}
                  />
                </Grid>
              </>
            ) : (
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Fixed amount"
                  value={itemForm.unitPrice}
                  onChange={(e) => setItemForm((f) => ({ ...f, unitPrice: e.target.value }))}
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={itemForm.active !== false}
                    onChange={(e) => setItemForm((f) => ({ ...f, active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemDialog(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<Save />} disabled={saving} onClick={saveItem}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </BillingPage>
  );
};

export default PricingPlans;
